import * as React from 'react';

import { cn } from '@/lib/cn';
import { SHELL_GUTTER, SIDEBAR_COLUMN_WIDTH } from './shell.logic';

export interface AppShellProps {
  /** `<Sidebar>…</Sidebar>`. Hidden below `lg`. */
  sidebar?: React.ReactNode;
  /** `<TopBar … />`. Hidden below `lg`. */
  topBar?: React.ReactNode;
  /** `<MobileTopBar … />`. Shown below `lg` only. */
  mobileBar?: React.ReactNode;
  /** Decorative layers behind the page (`<GridBand />`). */
  background?: React.ReactNode;
  /** Page content. Rendered inside the gutter. */
  children?: React.ReactNode;
  /** Under the page, at the bottom of the main column (legal links). */
  footer?: React.ReactNode;
  /**
   * Leave room under the content for the floating `SaveBar`. Not needed when
   * the bar is rendered inside the shell: the column then reserves it by itself.
   */
  reserveSaveBar?: boolean;
  /** Anything that floats over the shell and is not part of the page flow (`<SaveBar>`, `<MobileDrawer>`). */
  overlay?: React.ReactNode;
  className?: string;
}

/**
 * Frame of the panel: a `252px 1fr` grid with the sidebar sticky at full
 * height and the main column to its right (top bar, then the page). Below `lg`
 * the sidebar and the desktop top bar are replaced by `MobileTopBar` +
 * `MobileDrawer`. It paints the canvas with its two soft turquoise washes.
 */
export function AppShell({ sidebar, topBar, mobileBar, background, children, footer, reserveSaveBar, overlay, className }: AppShellProps) {
  return (
    <div
      data-slot="app-shell"
      className={cn('min-h-dvh text-ink lg:grid lg:grid-cols-[252px_1fr]', className)}
      style={{ ['--shell-sidebar-w' as string]: `${SIDEBAR_COLUMN_WIDTH}px`, ['--shell-gutter' as string]: SHELL_GUTTER }}
    >
      {sidebar ? <div className="hidden lg:block">{sidebar}</div> : null}
      <div data-slot="app-main" className="relative isolate flex min-h-dvh min-w-0 flex-col">
        {background}
        {mobileBar ? <div className="contents lg:hidden">{mobileBar}</div> : null}
        {topBar ? <div className="contents max-lg:hidden">{topBar}</div> : null}
        <main
          data-slot="app-page"
          className={cn('relative w-full flex-1 px-4 pt-8 lg:px-[var(--shell-gutter)]', reserveSaveBar ? 'pb-32' : 'pb-10')}
        >
          {children}
        </main>
        {footer}
        {overlay}
      </div>
    </div>
  );
}
